import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { BASE_URL } from "../../../config/api";
import axios from "axios";

export default function CategoryModal({ isOpen, onClose, onSubmit, initialData }) {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [status, setStatus] = useState(initialData?.status?.toString() || "1");
  const [preview, setPreview] = useState(initialData?.image || ""); // uploaded image URL
  const [loading, setLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false); // uploading state
  const [imageUploaded, setImageUploaded] = useState(
  Boolean(initialData?.image)
);


  useEffect(() => {
    setName(initialData?.name || "");
    setDescription(initialData?.description || "");
    setStatus(initialData?.status?.toString() || "1");
    setPreview(initialData?.image || "");
    setImageUploaded(Boolean(initialData?.image));
  }, [initialData]);

  if (!isOpen) return null;

  // ----------------- Handle image upload on select -----------------
  const handleImageChange = async (
    e
  ) => {

    const file =
      e.target.files[0];

    if (!file)
      return;

    setImageUploading(
      true
    );

    setImageUploaded(
      false
    );

    // 🔒 disable save until success

    try {

      const formData =
        new FormData();

      formData.append(
        "file",
        file
      );

      formData.append(
        "tag",
        "Category"
      );

      const response =
        await axios.post(
          `${BASE_URL}warranty/upload-file`,
          formData,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "multipart/form-data",
            },
          }
        );

      const data =
        response.data;

      if (
        data?.data
          ?.file_url
      ) {

        setPreview(
          data.data.file_url
        );

        setImageUploaded(
          true
        );

        // ✅ enable save
      }

    } catch (err) {

      console.error(
        err
      );

      alert(
        err?.response?.data
          ?.message ||
        err?.message ||
        "Image upload failed"
      );

      setImageUploaded(
        false
      );

    } finally {

      setImageUploading(
        false
      );

    }
  }; 

  // ----------------- Toggle Status -----------------
  const handleToggleStatus = async () => {

    const newStatus =
      status === "1"
        ? "0"
        : "1";

    setStatus(
      newStatus
    );

    if (
      initialData?.id
    ) {

      try {

        setToggleLoading(
          true
        );

        await axios.post(
          `${BASE_URL}warranty/category/toggle-status/${initialData.id}`,
          {},
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      } catch (err) {

        console.error(
          err
        );

        alert(
          err?.response?.data
            ?.message ||
          err?.message ||
          "Something went wrong"
        );

        setStatus(
          status
        );

        // revert on error

      } finally {

        setToggleLoading(
          false
        );

      }
    }
  };


  const resetForm = () => {
  setName("");
  setDescription("");
  setStatus("1");
  setPreview("");
  setImageUploaded(false);
};


  // ----------------- Submit Handler -----------------
  const handleSubmit = async (
    e
  ) => {

    e.preventDefault();

    setLoading(
      true
    );

    try {

      const payload = {

        name,

        description,

        image:
          preview,

        // use already uploaded image
        status:
          parseInt(
            status
          ),
      };

      if (
        initialData?.id
      ) {

        payload.id =
          initialData.id;

        await axios.post(
          `${BASE_URL}warranty/update-category/${initialData.id}`,
          payload,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );

      } else {

        await axios.post(
          `${BASE_URL}warranty/create-category`,
          payload,
          {
            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",
            },
          }
        );
      }

      resetForm();

      onSubmit();

      // refresh parent list

    } catch (err) {

      console.error(
        err
      );

      alert(
        err?.response?.data
          ?.message ||
        err?.message ||
        "Something went wrong"
      );

    } finally {

      setLoading(
        false
      );

    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50">
      <div className="bg-white rounded-xl w-96 p-6 shadow-lg relative">
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          onClick={onClose}
        >
          <X size={18} />
        </button>

        <h2 className="text-lg font-semibold mb-5">{initialData?.id ? "Update" : "Add"} Category</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm mb-1">Category Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. New Category"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm mb-1">Upload Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full text-sm"
            />
            {preview && (
              <div className="mt-3 relative">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-24 h-24 object-cover rounded-md border border-gray-300"
                />
                {imageUploading && <p className="text-xs text-gray-500 mt-1">Uploading...</p>}
              </div>
            )}
          </div>

          {/* Toggle Status */}
          <div className="flex items-center gap-2 mt-2">
            <label className="text-sm">Status:</label>
            <button
              type="button"
              onClick={handleToggleStatus}
              disabled={toggleLoading}
              className={`px-3 py-1 text-xs rounded-md ${
                status === "1" ? "bg-green-500 text-white" : "bg-red-500 text-white"
              }`}
            >
              {toggleLoading ? "..." : status === "1" ? "Active" : "Inactive"}
            </button>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || imageUploading || !imageUploaded}
            className={`w-full py-2 rounded-md font-semibold transition-colors
              ${
                loading || imageUploading || !imageUploaded
                  ? "bg-slate-500 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }
            `}
          >
            {loading ? "Saving..." : "Save Category"}
          </button>
        </form>
      </div>
    </div>
  );
}





// import React, { useState, useEffect } from "react";
// import { X } from "lucide-react";

// export default function CategoryModal({ isOpen, onClose, onSubmit, initialData }) {
//   const [name, setName] = useState(initialData?.name || "");
//   const [description, setDescription] = useState(initialData?.description || "");
//   const [status, setStatus] = useState(initialData?.status?.toString() || "1");
//   const [imageFile, setImageFile] = useState(null); // File object
//   const [preview, setPreview] = useState(initialData?.image || ""); // Can be URL
//   const [loading, setLoading] = useState(false);
//   const [toggleLoading, setToggleLoading] = useState(false);

//   useEffect(() => {
//     setName(initialData?.name || "");
//     setDescription(initialData?.description || "");
//     setStatus(initialData?.status?.toString() || "1");
//     setPreview(initialData?.image || "");
//     setImageFile(null);
//   }, [initialData]);

//   // Dynamically generate preview for selected file
//   useEffect(() => {
//     if (imageFile) {
//       const objectUrl = URL.createObjectURL(imageFile);
//       setPreview(objectUrl);
//       return () => URL.revokeObjectURL(objectUrl);
//     }
//   }, [imageFile]);

//   if (!isOpen) return null;

//   // ----------------- Toggle Status -----------------
//   const handleToggleStatus = async () => {
//     const newStatus = status === "1" ? "0" : "1";
//     setStatus(newStatus);

//     if (initialData?.id) {
//       try {
//         setToggleLoading(true);
//         const res = await fetch(
//           `https://goelectronix.in/api/v1/warranty/category/toggle-status/${initialData.id}`,
//           { method: "POST" }
//         );
//         if (!res.ok) throw new Error("Failed to toggle status");
//       } catch (err) {
//         alert(err.message || "Something went wrong");
//         setStatus(status); // revert on error
//       } finally {
//         setToggleLoading(false);
//       }
//     }
//   };

//   // ----------------- Submit Handler -----------------
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);

//     try {
//       let uploadedImage = preview;

//       // Upload file if a new one is selected
//       if (imageFile) {
//         const formData = new FormData();
//         formData.append("file", imageFile);
//         formData.append("tag", "Category");

//         const res = await fetch("https://goelectronix.in/api/v1/warranty/upload-file", {
//           method: "POST",
//           body: formData,
//         });

//         if (!res.ok) throw new Error("Image upload failed");
//         const data = await res.json();
//         uploadedImage = data?.file_url; // dynamic URL from server
//       }

//       const payload = {
//         name,
//         description,
//         image: uploadedImage, // dynamic URL
//         status: parseInt(status),
//       };

//       // Call correct API
//       if (initialData?.id) {
//         payload.id = initialData.id;
//         const res = await fetch("https://goelectronix.in/api/v1/warranty/update-category", {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload),
//         });
//         if (!res.ok) throw new Error("Update failed");
//       } else {
//         const res = await fetch("https://goelectronix.in/api/v1/warranty/create-category", {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload),
//         });
//         if (!res.ok) throw new Error("Create failed");
//       }

//       onSubmit(); // refresh parent list
//     } catch (err) {
//       alert(err.message || "Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50">
//       <div className="bg-white rounded-xl w-96 p-6 shadow-lg relative">
//         <button
//           className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
//           onClick={onClose}
//         >
//           <X size={18} />
//         </button>

//         <h2 className="text-lg font-semibold mb-5">{initialData?.id ? "Update" : "Add"} Category</h2>

//         <form onSubmit={handleSubmit} className="space-y-4">
//           {/* Name */}
//           <div>
//             <label className="block text-sm mb-1">Category Name</label>
//             <input
//               type="text"
//               value={name}
//               onChange={(e) => setName(e.target.value)}
//               placeholder="e.g. New Category"
//               className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
//               required
//             />
//           </div>

//           {/* Description */}
//           <div>
//             <label className="block text-sm mb-1">Description</label>
//             <textarea
//               value={description}
//               onChange={(e) => setDescription(e.target.value)}
//               placeholder="Optional description"
//               className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
//             />
//           </div>

//           {/* Image Upload */}
//           <div>
//             <label className="block text-sm mb-1">Upload Image</label>
//             <input
//               type="file"
//               accept="image/*"
//               onChange={(e) => setImageFile(e.target.files[0])}
//               className="w-full text-sm"
//             />
//             {preview && (
//               <div className="mt-3">
//                 <img
//                   src={preview}
//                   alt="Preview"
//                   className="w-24 h-24 object-cover rounded-md border border-gray-300"
//                 />
//               </div>
//             )}
//           </div>

//           {/* Toggle Status */}
//           <div className="flex items-center gap-2 mt-2">
//             <label className="text-sm">Status:</label>
//             <button
//               type="button"
//               onClick={handleToggleStatus}
//               disabled={toggleLoading}
//               className={`px-3 py-1 text-xs rounded-md ${
//                 status === "1" ? "bg-green-500 text-white" : "bg-red-500 text-white"
//               }`}
//             >
//               {toggleLoading ? "..." : status === "1" ? "Active" : "Inactive"}
//             </button>
//           </div>

//           {/* Submit */}
//           <button
//             type="submit"
//             disabled={loading}
//             className="w-full bg-blue-600 text-white py-2 rounded-md font-semibold hover:bg-blue-700 transition-colors"
//           >
//             {loading ? "Saving..." : "Save Category"}
//           </button>
//         </form>
//       </div>
//     </div>
//   );
// }




// import React, { useState, useEffect } from "react";
// import { X } from "lucide-react";

// export default function CategoryModal({ isOpen, onClose, onSubmit, initialData }) {
//   const [name, setName] = useState(initialData?.name || "");
//   const [description, setDescription] = useState(initialData?.description || "");
//   const [status, setStatus] = useState(initialData?.status?.toString() || "1");
//   const [image, setImage] = useState(null);
//   const [preview, setPreview] = useState(initialData?.image || null);
//   const [loading, setLoading] = useState(false);
//   const [toggleLoading, setToggleLoading] = useState(false); // For toggle button

//   useEffect(() => {
//     setName(initialData?.name || "");
//     setDescription(initialData?.description || "");
//     setStatus(initialData?.status?.toString() || "1");
//     setPreview(initialData?.image || null);
//     setImage(null);
//   }, [initialData]);

//   useEffect(() => {
//     if (image) {
//       const objectUrl = URL.createObjectURL(image);
//       setPreview(objectUrl);
//       return () => URL.revokeObjectURL(objectUrl);
//     }
//   }, [image]);

//   if (!isOpen) return null;

//   // ----------------- HANDLE STATUS TOGGLE -----------------
//   const handleToggleStatus = async () => {
//     const newStatus = status === "1" ? "0" : "1";
//     setStatus(newStatus);

//     if (initialData?.id) {
//       try {
//         setToggleLoading(true);
//         const res = await fetch(
//           `https://goelectronix.in/api/v1/warranty/category/toggle-status/${initialData.id}`,
//           { method: "POST" }
//         );
//         if (!res.ok) throw new Error("Failed to toggle status");
//       } catch (err) {
//         alert(err.message || "Something went wrong");
//         setStatus(status); // revert on error
//       } finally {
//         setToggleLoading(false);
//       }
//     }
//   };

//   // ----------------- HANDLE SUBMIT -----------------
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);

//     try {
//       let uploadedImage = preview;

//       // 1️⃣ Upload image if selected
//       if (image) {
//         const formData = new FormData();
//         formData.append("file", image);
//         formData.append("tag", "Category");

//         const res = await fetch("https://goelectronix.in/api/v1/warranty/upload-file", {
//           method: "POST",
//           body: formData,
//         });

//         if (!res.ok) throw new Error("Image upload failed");
//         const data = await res.json();
//         uploadedImage = data?.file_url; // Assuming API returns uploaded file URL
//       }

//       // 2️⃣ Prepare payload
//       const payload = {
//         name,
//         description,
//         image: uploadedImage,
//         status: parseInt(status),
//       };

//       // 3️⃣ Call the correct API
//       if (initialData?.id) {
//         // Update
//         payload.id = initialData.id;
//         const res = await fetch("https://goelectronix.in/api/v1/warranty/update-category", {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload),
//         });
//         if (!res.ok) throw new Error("Update failed");
//       } else {
//         // Create
//         const res = await fetch("https://goelectronix.in/api/v1/warranty/create-category", {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload),
//         });
//         if (!res.ok) throw new Error("Create failed");
//       }

//       onSubmit(); // Refresh parent list
//     } catch (err) {
//       alert(err.message || "Something went wrong");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50">
//       <div className="bg-white rounded-xl w-96 p-6 shadow-lg relative">
//         <button
//           className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
//           onClick={onClose}
//         >
//           <X size={18} />
//         </button>

//         <h2 className="text-lg font-semibold mb-5">{initialData?.id ? "Update" : "Add"} Category</h2>

//         <form onSubmit={handleSubmit} className="space-y-4">
//           {/* Name */}
//           <div>
//             <label className="block text-sm mb-1">Category Name</label>
//             <input
//               type="text"
//               value={name}
//               onChange={(e) => setName(e.target.value)}
//               placeholder="e.g. New Category"
//               className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
//               required
//             />
//           </div>

//           {/* Description */}
//           <div>
//             <label className="block text-sm mb-1">Description</label>
//             <textarea
//               value={description}
//               onChange={(e) => setDescription(e.target.value)}
//               placeholder="Optional description"
//               className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
//             />
//           </div>

//           {/* Image */}
//           <div>
//             <label className="block text-sm mb-1">Upload Image</label>
//             <input
//               type="file"
//               accept="image/*"
//               onChange={(e) => setImage(e.target.files[0])}
//               className="w-full text-sm"
//             />
//             {preview && (
//               <div className="mt-3">
//                 <img
//                   src={preview}
//                   alt="Preview"
//                   className="w-24 h-24 object-cover rounded-md border border-gray-300"
//                 />
//               </div>
//             )}
//           </div>

//           {/* Toggle Status */}
//           <div className="flex items-center gap-2 mt-2">
//             <label className="text-sm">Status:</label>
//             <button
//               type="button"
//               onClick={handleToggleStatus}
//               disabled={toggleLoading}
//               className={`px-3 py-1 text-xs rounded-md ${
//                 status === "1" ? "bg-green-500 text-white" : "bg-red-500 text-white"
//               }`}
//             >
//               {toggleLoading ? "..." : status === "1" ? "Active" : "Inactive"}
//             </button>
//           </div>

//           {/* Submit */}
//           <button
//             type="submit"
//             disabled={loading}
//             className="w-full bg-blue-600 text-white py-2 rounded-md font-semibold hover:bg-blue-700 transition-colors"
//           >
//             {loading ? "Saving..." : "Save Category"}
//           </button>
//         </form>
//       </div>
//     </div>
//   );
// }




// import React, { useState, useEffect } from "react";
// import { X } from "lucide-react";

// export default function CategoryModal({ isOpen, onClose, onSubmit, initialData }) {
//   const [name, setName] = useState(initialData?.name || "");
//   const [description, setDescription] = useState(initialData?.description || "");
//   const [status, setStatus] = useState(initialData?.status || "1");
//   const [image, setImage] = useState(null);
//   const [preview, setPreview] = useState(initialData?.image || null);

//   useEffect(() => {
//     setName(initialData?.name || "");
//     setDescription(initialData?.description || "");
//     setStatus(initialData?.status || "1");
//     setPreview(initialData?.image || null);
//   }, [initialData]);

//   useEffect(() => {
//     if (image) {
//       const objectUrl = URL.createObjectURL(image);
//       setPreview(objectUrl);

//       // Cleanup
//       return () => URL.revokeObjectURL(objectUrl);
//     }
//   }, [image]);

//   if (!isOpen) return null;

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     const formData = { name, description, status, image };
//     onSubmit(formData);
//   };

//   return (
//     <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50">
//       <div className="bg-white rounded-xl w-96 p-6 shadow-lg relative">
//         <button
//           className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
//           onClick={onClose}
//         >
//           <X size={18} />
//         </button>

//         <h2 className="text-lg font-semibold mb-5">Add / Update Category</h2>

//         <form onSubmit={handleSubmit} className="space-y-4">
//           {/* Category Name */}
//           <div>
//             <label className="block text-sm mb-1">Category Name</label>
//             <input
//               type="text"
//               value={name}
//               onChange={(e) => setName(e.target.value)}
//               placeholder="e.g. New Category"
//               className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
//               required
//             />
//           </div>

//           {/* Description */}
//           <div>
//             <label className="block text-sm mb-1">Description</label>
//             <textarea
//               value={description}
//               onChange={(e) => setDescription(e.target.value)}
//               placeholder="Optional description"
//               className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
//             />
//           </div>

//           {/* Image Upload */}
//           <div>
//             <label className="block text-sm mb-1">Upload Image</label>
//             <input
//               type="file"
//               accept="image/*"
//               onChange={(e) => setImage(e.target.files[0])}
//               className="w-full text-sm"
//             />
//             {preview && (
//               <div className="mt-3">
//                 <img
//                   src={preview}
//                   alt="Preview"
//                   className="w-24 h-24 object-cover rounded-md border border-gray-300"
//                 />
//               </div>
//             )}
//           </div>

//           {/* Status */}
//           <div>
//             <label className="block text-sm mb-1">Status</label>
//             <select
//               value={status}
//               onChange={(e) => setStatus(e.target.value)}
//               className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
//             >
//               <option value="1">Active</option>
//               <option value="0">Inactive</option>
//             </select>
//           </div>

//           {/* Submit Button */}
//           <button
//             type="submit"
//             className="w-full bg-blue-600 text-white py-2 rounded-md font-semibold hover:bg-blue-700 transition-colors"
//           >
//             Save Category
//           </button>
//         </form>
//       </div>
//     </div>
//   );
// }
